import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/shared/components/shadcn/Button";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { useStripeConnection } from "../hooks/stripeQueries";
import { useStripeConnect } from "../hooks/useStripeConnect";
import { StripeBenefits } from "./StripeBenefits";
import { StripeConnectionCard } from "./StripeConnectionCard";

interface StripeSettingsProps {
  businessId: string;
  businessName: string;
  locationId: string;
  userId: string;
}

export function StripeSettings({ businessId, businessName, locationId, userId }: StripeSettingsProps) {
  const outcome: unknown = useLocation().state?.stripeOutcome;
  const query = useStripeConnection(businessId, userId);
  const flow = useStripeConnect(businessId, locationId, userId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const connection = query.data;
  const returnMessage = outcome === "expired"
    ? "Your setup link expired. Check the status below and continue setup if needed."
    : outcome === "error"
      ? "Stripe setup could not finish. Check the status below before trying again."
      : outcome === "returned"
        ? "Returned from Stripe. The status below comes from Froppio's account check, not from the return link."
        : undefined;

  const connect = () => {
    if (connection?.status === "not_connected") {
      setConfirmOpen(true);
    } else {
      void flow.connect();
    }
  };

  return (
    <div className="space-y-6">
      <StripeBenefits connected={!query.error && connection?.status === "connected"} />
      {returnMessage && <p role="status" className="text-sm leading-relaxed text-muted-foreground">{returnMessage}</p>}
      {query.isPending ? (
        <LoadingSpinner message="Loading Stripe connection..." />
      ) : query.error ? (
        <ErrorState
          title="Couldn't load Stripe connection"
          message="Connection status is unavailable. Please try again."
          onRetry={() => void query.refetch()}
        />
      ) : connection && (
        <StripeConnectionCard connection={connection} businessName={businessName} busy={flow.busy} onConnect={connect} />
      )}
      {flow.message && <p role="status" className="text-sm leading-relaxed">{flow.message}</p>}
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-muted-foreground">
          Connecting Stripe does not enable charging customers in Froppio yet.
        </p>
        <Button variant="quiet" disabled={query.isFetching} onClick={() => void query.refetch()} className="shrink-0">
          Refresh status
        </Button>
      </div>
      <ConfirmationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Confirm your business country"
        description="This setup is for a United States business account. Confirm that your business is based in the US. Stripe collects and verifies your business details in your browser, not in Froppio."
        confirmLabel="My business is in the US"
        dismissLabel="Cancel"
        onConfirm={() => {
          setConfirmOpen(false);
          void flow.connect();
        }}
      />
    </div>
  );
}
